import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const INFO = [
    { icon: Mail, label: "Email", value: "hello@sweetpearls.com" },
    { icon: Phone, label: "Phone", value: "(555) 012-3456" },
    { icon: MapPin, label: "Kitchen", value: "128 Vanilla Lane, Sweetville" },
    { icon: Clock, label: "Hours", value: "Tue–Sun · 9am–7pm" },
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            setForm({ name: "", email: "", message: "" });
            toast.success("Message sent! We'll reply within one business day. 💌");
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
            <Reveal className="text-center">
                <p className="text-sm font-semibold uppercase tracking-widest text-caramel">Contact</p>
                <h1 className="mt-2 font-serif text-4xl font-semibold text-roast sm:text-5xl">We'd love to hear from you</h1>
                <p className="mt-3 text-mocha">Questions, custom orders, or just want to say hi? Reach out anytime.</p>
            </Reveal>

            <div className="mt-12 grid gap-10 lg:grid-cols-2">
                <Reveal>
                    <div className="grid grid-cols-2 gap-4">
                        {INFO.map((c) => (
                            <div key={c.label} className="rounded-2xl border border-caramel/10 bg-white p-5 shadow-warm">
                                <c.icon size={22} className="text-caramel" />
                                <p className="mt-3 text-sm text-mocha">{c.label}</p>
                                <p className="font-medium text-roast">{c.value}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 overflow-hidden rounded-2xl">
                        <img src="/img/caramel.jpg" alt="Salted caramel pudding" className="h-56 w-full object-cover" />
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <form onSubmit={submit} className="rounded-3xl border border-caramel/10 bg-white p-8 shadow-warm" data-testid="contact-form">
                        <div className="space-y-4">
                            <Input required data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" className="h-12 rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                            <Input required type="email" data-testid="contact-email" value={form.email} onChange={set("email")} placeholder="Email" className="h-12 rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                            <Textarea required data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="How can we help?" className="min-h-[140px] rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                            <Button type="submit" disabled={loading} data-testid="contact-submit" className="h-12 w-full rounded-full bg-caramel text-base text-cream-50 hover:bg-caramel-dark">{loading ? "Sending…" : "Send message"}</Button>
                        </div>
                    </form>
                </Reveal>
            </div>
        </div>
    );
}
